import { useEffect, useMemo } from 'react'

import type {
  Condition,
  ConditionalOperator,
  ConditionPropertyEditorComponentProps
} from 'Automations/nodeDefinitions/conditions'
import {
  isQuotePricingComparison,
  isQuoteComparisonSubOperator,
  type QuoteTemplateConditionValue,
  type QuotePricingComparison,
  type QuoteComparisonSubOperator
} from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'
import {
  expressionFor,
  expressionOptions,
  getRemainingExpressions,
  ownExpressionOf,
  hasPricingComparisons,
  isExhausted,
  isQuoteComparisonField,
  parseExpression,
  DEFAULT_SUB_OPERATOR
} from '../quoteComparison'

/**
 * What a QUOTE_TEMPLATE row stores, with every field normalised so `undefined`
 * is the only way to say "unset". A stored row can carry an empty string, or a
 * comparison from a field that no longer offers one, and neither should read as
 * a choice the user made.
 */
export type QuoteTemplateRow = {
  templateId: string | undefined
  subOperator: QuoteComparisonSubOperator | undefined
  comparison: QuotePricingComparison | undefined
}

export const readRow = (value: unknown): QuoteTemplateRow => {
  const stored: QuoteTemplateConditionValue =
    typeof value === 'object' && value !== null
      ? (value as QuoteTemplateConditionValue)
      : {}

  return {
    templateId: stored.templateId || undefined,
    subOperator: isQuoteComparisonSubOperator(stored.operator)
      ? stored.operator
      : undefined,
    comparison: isQuotePricingComparison(stored.comparison)
      ? stored.comparison
      : undefined
  }
}

/**
 * The expressions sibling rows already contribute on one template. A sibling
 * with no template or no field yet contributes nothing.
 */
export const usedExpressionsOnTemplate = ({
  siblingConditions,
  templateId
}: {
  siblingConditions: Array<Condition>
  templateId: string | undefined
}): string[] =>
  templateId
    ? siblingConditions.reduce<string[]>((used, sibling) => {
        if (
          !sibling.operator ||
          readRow(sibling.value).templateId !== templateId
        ) {
          return used
        }
        return [
          ...used,
          expressionFor({ operator: sibling.operator, value: sibling.value })
        ]
      }, [])
    : []

/**
 * Everything a QUOTE_TEMPLATE row's editor needs: which fields and comparisons
 * are still free, and what the row effectively holds once defaults and
 * sibling-driven fallbacks are applied.
 *
 * Cross-row rules are scoped per template and per expression. Several rows may
 * compare the same template — its prices and its discounts are different
 * questions — but no two may take the same expression, and a template is exhausted
 * only once every expression is.
 *
 * The effective values are committed back as soon as the row has a template and
 * a field, so what is stored matches what is shown.
 */
export const useQuoteTemplateOperatorState = (params: {
  operator: string
  row: QuoteTemplateRow
  operators: Record<string, ConditionalOperator>
  siblingConditions: Array<Condition>
  onChange: ConditionPropertyEditorComponentProps['onChange']
}): {
  /** Every way of comparing still free, pricing's comparisons among them. */
  expressionOptions: Array<{ label: string; value: string }>
  expressionDisabled: boolean
  /** The one the row reads as, as a value for the dropdown. */
  expression: string
  subOperator: QuoteComparisonSubOperator
  exhaustedTemplateIds: string[]
} => {
  const { operator, row, operators, siblingConditions, onChange } = params
  const { templateId } = row

  const usedExpressions = usedExpressionsOnTemplate({
    siblingConditions,
    templateId
  })
  const showComparisons = hasPricingComparisons(operator)
  const remainingExpressions = getRemainingExpressions({
    usedExpressions,
    ownExpression: ownExpressionOf({ operator, comparison: row.comparison })
  })
  // This row's own pick stays listed, so a stored condition still renders and
  // the user can switch back to it.
  const options = expressionOptions({ remainingExpressions, operators })

  // A pricing row with no comparison takes the first still free, so it can't
  // duplicate a sibling. 'ALL' needs a template with none free — unreachable
  // from the editor.
  const comparison =
    row.comparison ?? firstFreePricingComparison(remainingExpressions) ?? 'ALL'
  const subOperator = row.subOperator ?? DEFAULT_SUB_OPERATOR

  const storedComparison = showComparisons ? comparison : undefined
  const fieldSelected = isQuoteComparisonField(operator)

  useEffect(() => {
    if (!templateId || !fieldSelected) return
    if (
      row.subOperator === subOperator &&
      row.comparison === storedComparison
    ) {
      return
    }

    onChange({
      value: { templateId, operator: subOperator, comparison: storedComparison }
    })
  }, [
    templateId,
    fieldSelected,
    row.subOperator,
    row.comparison,
    subOperator,
    storedComparison,
    onChange
  ])

  const exhaustedTemplateIds = useMemo(
    () =>
      siblingTemplateIds(siblingConditions).filter(
        id =>
          id !== templateId &&
          isExhausted(
            usedExpressionsOnTemplate({ siblingConditions, templateId: id })
          )
      ),
    [siblingConditions, templateId]
  )

  return {
    expressionOptions: options,
    expressionDisabled: options.length === 0,
    expression: fieldSelected
      ? expressionFor({ operator, value: { comparison: storedComparison } })
      : '',
    subOperator,
    exhaustedTemplateIds
  }
}

/** The first pricing comparison a subject still has free, if any. */
const firstFreePricingComparison = (
  remainingExpressions: string[]
): QuotePricingComparison | undefined =>
  remainingExpressions.reduce<QuotePricingComparison | undefined>(
    (found, expression) => found ?? parseExpression(expression).comparison,
    undefined
  )

/** Every template a sibling row targets, once each. */
const siblingTemplateIds = (siblingConditions: Array<Condition>): string[] =>
  Array.from(
    new Set(
      siblingConditions.reduce<string[]>((ids, sibling) => {
        const { templateId } = readRow(sibling.value)
        return templateId ? [...ids, templateId] : ids
      }, [])
    )
  )
